// 채팅 페이지 텍스트 유틸 — 바닐라 app.js 의 extractChoices/stripMarkers/
// 꼬리질문 교정/summarizeTitle/진행문구 변환/날짜 라벨/nowTime 이식 (Phase 3-A).

/** 평가 대상 (한 대화에서 여러 기업 누적) — 스냅샷 targets 필드와 동일 형태 */
export interface VaTarget {
  name: string;
  ticker: string;
}

export interface ParsedReply {
  text: string;
  choices: string[] | null;
  next: string[] | null;
  title: string | null;
}

/** 답변에서 마커 줄을 분리 (줄 앞뒤 어디서든 매칭)
 *    [[TITLE]]   모델이 지은 대화 제목
 *    [[CHOICES]] 되묻기 — 골라야 진행된다
 *    [[NEXT]]    꼬리 질문 — 골라도 되고 말아도 된다 */
export function extractChoices(raw: string): ParsedReply {
  const lines = String(raw || "").split("\n");
  let choices: string[] | null = null;
  let next: string[] | null = null;
  let title: string | null = null;
  const kept: string[] = [];
  const parts = (t: string) =>
    t
      .split("|")
      .map((x) => x.replace(/\*\*/g, "").trim())
      .filter(Boolean);
  for (const ln of lines) {
    // bold 마크다운 ** 안에 있어도 처리
    const clean = ln.replace(/\*\*/g, "").trim();
    const c = clean.match(/\[\[CHOICES\]\]\s*(.+)$/i);
    const n = clean.match(/\[\[NEXT\]\]\s*(.+)$/i);
    const t = clean.match(/\[\[TITLE\]\]\s*(.+)$/i);
    if (c) choices = parts(c[1]);
    else if (n) next = parts(n[1]);
    else if (t) title = t[1].replace(/\*\*/g, "").trim().slice(0, 30);
    else kept.push(ln);
  }
  // 되묻는 중이면 꼬리 질문은 내리지 않는다 — 고를 것이 두 종류면 헷갈린다
  if (choices) next = null;
  return { text: kept.join("\n").trim(), choices, next, title };
}

/** 스트리밍 중 마커 줄이 아직 완성되기 전 — 줄 첫머리의 [[ 이후를 통째로 감춘다 */
export function stripMarkers(t: string): string {
  return extractChoices(t).text.replace(/(^|\n)\s*\**\[\[[^\n]*$/, "");
}

/** 꼬리 질문 문구 교정 — 모델이 자기 목소리("~찾아줄까요")로 쓰면
 *  사용자 목소리("~찾아줘")로. 버튼 문구는 클릭 시 그대로 전송되는 사용자의 말이다. */
export function normalizeNextChip(q: string): string {
  return String(q)
    .trim()
    .replace(/([가-힣])\s*(드릴|줄)까요\??$/u, "$1줘") // 찾아줄까요→찾아줘
    .replace(/해\s*볼까요\??$/u, "해줘")
    .replace(/할까요\??$/u, "해줘")
    .replace(/볼까요\??$/u, "봐줘")
    .replace(/(하시겠어요|어떠세요|어때요)\??$/u, "해줘")
    .replace(/\?+$/u, "")
    .trim();
}

// ── 대화 제목 — 첫 질문을 그대로 쓰지 않고 의도를 짧게 요약 ──
// 발굴형(조건으로 기업 찾기)은 특정 기업명을 붙이지 않는다.
const TITLE_INTENTS: Array<[RegExp, string, boolean?]> = [
  [/찾아|검색|스크리닝|발굴|보유 기업/i, "기업 발굴", true],
  [/상대\s*가치|relative|peer\s*multiple/i, "상대가치평가"],
  [/\bdcf\b|현금흐름할인/i, "DCF 평가"],
  [/wacc|할인율/i, "WACC 계산"],
  [/베타|beta|\bβ\b/i, "베타 계산"],
  [/rcps|전환우선주/i, "RCPS 평가"],
  [/스톡\s*옵션|stock\s*option/i, "스톡옵션 평가"],
  [/버핏/i, "버핏 관점 분석"],
  [/peer|유사기업|동종업계|경쟁기업/i, "유사기업 선정"],
  [/공시/i, "공시 조회"],
  [/뉴스/i, "뉴스 조회"],
  [/리서치|보고서/i, "리서치 조회"],
  [/특허/i, "특허 조회"],
  [/주주|지분/i, "주주 현황"],
  [/주가/i, "주가 조회"],
  [/엑셀|excel|파일|다운로드/i, "엑셀 산출"],
  [/재무|매출|영업이익|실적/i, "재무 조회"],
  [/기본\s*정보|개요|프로필/i, "기업 개요"],
];
// 질문에서 회사명 추출 — 이미 알고 있는 대상 우선, 없으면 첫 어절 휴리스틱
const TITLE_STOPWORDS =
  /^(매출|영업이익|당기순이익|자산|사업|최근|오늘|어제|전체|국내|해외|상장|비상장|동종업계|양자컴퓨터|반도체|이번|다음|워런|버핏|우리|내가|이거|그거)/;

function companyInText(t: string, targets: VaTarget[]): string | null {
  const known = targets.find((x) => t.includes(x.name));
  if (known) return known.name;
  const obj = (t.match(/([가-힣A-Za-z0-9&().]{2,12})(?:을|를|의|에 대해|에대해)\s/) ||
    [])[1];
  if (obj && !TITLE_STOPWORDS.test(obj)) return obj;
  const first = (t.match(/^[가-힣A-Za-z0-9&().]{2,12}/) || [])[0];
  if (first && !TITLE_STOPWORDS.test(first) && /[가-힣A-Za-z]/.test(first))
    return first;
  return null;
}

export function summarizeTitle(text: string, targets: VaTarget[] = []): string {
  const t = String(text || "").trim().replace(/\s+/g, " ");
  if (!t) return "새 채팅";
  const hit = TITLE_INTENTS.find(([re]) => re.test(t));
  const intent = hit ? hit[1] : null;
  const noCompany = hit ? !!hit[2] : false;
  // 발굴형은 조건이 곧 의도 — 질문의 조건부를 요약해 쓴다
  if (noCompany) {
    let cond = t
      .replace(/(찾아줘|찾아 줘|검색해줘|검색해 줘|알려줘|보여줘|해줘|해주세요|줘)[.!?]*$/u, "")
      .trim();
    if (cond.length > 20) {
      // 단어 경계에서 자른다 — "매출 100억"이 "매출 100"으로 잘리지 않게
      const cut = cond.slice(0, 22);
      cond = cut.slice(0, Math.max(10, cut.lastIndexOf(" "))).trim();
    }
    if (!cond) return "기업 발굴";
    return /(상장사|기업|회사|법인|외감)$/.test(cond) ? cond + " 발굴" : cond + " 기업 발굴";
  }
  const company = companyInText(t, targets);
  if (company && intent) return `${company} ${intent}`;
  if (intent) return intent;
  if (company) return company;
  const trimmed = t
    .replace(
      /(해줘|해 줘|알려줘|보여줘|해주세요|알려주세요|해라|해줄래|하고 싶어|하고싶어|줘)[.!?]*$/u,
      "",
    )
    .trim();
  return (trimmed || t).slice(0, 24);
}

/** 표시용 상호 — "효성티앤씨(주)"·"주식회사 효성티앤씨" → "효성티앤씨" */
export function cleanCompanyName(n: string): string {
  return (
    String(n || "")
      .replace(/\s*\((주|유|재|사)\)\s*/g, "")
      .replace(/^주식회사\s*/, "")
      .replace(/\s*주식회사$/, "")
      .trim() || String(n || "").trim()
  );
}

// ── 진행 로그 문구 변환 ──────────────────────────────────
/** 경과시간 등 가변 꼬리를 제거한 비교용 키 — 같은 내용의 반복 갱신 판정 */
export function progressKey(s: string): string {
  return String(s)
    .replace(/\s*\([^)]*경과\)\s*$/, "")
    .replace(/\s*\d+\s*초.*$/, "")
    .trim();
}

/** 진행 중 표현을 완료형으로 — "주가 조회 중…" → "주가 조회 완료".
 *  내용 없는 라인("다음 단계 판단 중")은 빈 문자열을 돌려 버리게 한다. */
export function finishedPhrase(t: string): string {
  const s0 = String(t || "");
  if (/^(다음 단계 판단|분석)\s*중/u.test(s0)) return "";
  return s0
    .replace(/\s*중[.…·]*\s*$/u, " 완료")
    .replace(/\s*중[.…·]*(?=\s*[:：(])/u, " 완료")
    .replace(/\s*하는 중\b/u, " 완료")
    .trim();
}

/** 실패한 단계 문구 — "…조회 중" → "…조회 실패" */
export function failedPhrase(t: string): string {
  const s0 = String(t || "");
  if (/^(다음 단계 판단|분석)\s*중/u.test(s0)) return "";
  return (
    s0
      .replace(/\s*중[.…·]*\s*$/u, " 실패")
      .replace(/\s*중[.…·]*(?=\s*[:：(])/u, " 실패")
      .replace(/\s*완료\s*$/u, " 실패")
      .trim() || s0
  );
}

/** 사이드바 날짜 라벨: 오늘 → 시간만, 올해 → "M월 D일", 그 이전 → YYYY-MM-DD */
export function sessionDateLabel(s: { tsMs?: number; ts?: string }): string {
  const ms = s && Number(s.tsMs);
  if (!ms || isNaN(ms)) return s && s.ts ? s.ts : ""; // 구버전(tsMs 없음) 호환
  const d = new Date(ms);
  const now = new Date();
  const startOf = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (dayDiff <= 0)
    return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  if (d.getFullYear() === now.getFullYear())
    return d.getMonth() + 1 + "월 " + d.getDate() + "일";
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

export function nowTime(): string {
  return new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
}

/** 도구 호출 서명 — 어떤 도구를 어떤 인자로 불렀는지 감사용 한 줄.
 *  예: stock_price(symbol="KRX:005930", date_from="2026-01-01") */
export function formatToolCall(tool: string, input: unknown): string {
  if (!tool) return "";
  const args: string[] = [];
  if (input && typeof input === "object" && !Array.isArray(input)) {
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      if (v === undefined || v === null || v === "") continue;
      let out: string;
      if (typeof v === "string") out = `"${v.length > 48 ? v.slice(0, 48) + "…" : v}"`;
      else if (Array.isArray(v)) out = v.length <= 3 ? JSON.stringify(v) : `[${v.length}개]`;
      else if (typeof v === "object") out = "{…}";
      else out = String(v);
      args.push(`${k}=${out}`);
      if (args.length >= 6) { args.push("…"); break; }
    }
  }
  return `${tool}(${args.join(", ")})`;
}
